/**
 * #280 — game-local position snapshot (pure projection).
 *
 * Answers one question for the trading card: **on the market this panel is
 * currently pointed at, what do I hold and what did I pay?** Read-only. No
 * P&L, no order actions, no ledger writes.
 *
 * Deliberately NOT an accounting authority beyond that question. The
 * match-level `PairContextCard` alongside it folds blended pair/residual
 * economics from the fills ledger; this module reports the per-market
 * canonical figures the Positions page reports, and the two are allowed to
 * differ in framing because they are answering different questions.
 *
 * ── Market identity is exact ────────────────────────────────────────────────
 * Kalshi matches by ticker, Polymarket by condition id, both taken from the
 * panel's EFFECTIVE game (`GameDetailPanel`'s chip-aware `game` binding, not
 * the original prop). Never by team text, slug, base-game key, or "the same
 * bet elsewhere". A spread holding must not appear on the moneyline chip, and
 * a Kalshi holding must never be folded into a Polymarket one — they settle
 * on different paper.
 *
 * Orientation is derived from the TICKER, not from the game row's slot. A
 * spread synth row carries one ticker in both its away and home slots
 * (`SpreadsTotals/synthesize.ts`), and the Kalshi spread market's YES is the
 * FAVORED team — so labelling by slot printed the away team on a home-favored
 * line, contradicting the trade ticket directly above this card. `describeMarket`
 * is the canonical helper for that (injected, see `kalshiOutcomeLabel`).
 *
 * ── A missing average is null, never 0 ──────────────────────────────────────
 * Kalshi averages arrive already normalized by the component's adapter over
 * `accountStore.positions`, the same source Position Tracker renders, and pass
 * through untouched. Polymarket does
 * not: the relay emits `0` as its no-basis value
 * (`relay/src/services/positions.ts:839`), and Position Tracker still prints
 * that sentinel as "0¢". This module is the first place that normalization is
 * encapsulated — a non-positive or non-finite average becomes `null`, and the
 * count stays visible beside it.
 *
 * Polymarket's exact `costBasisCents` is preferred over `avgEntryPriceCents`
 * for deriving the average. Note this buys no PRECISION on the average itself:
 * the relay has emitted `avgEntryPriceCents` at 0.01¢ grain since the
 * whole-cent quantize was fixed in prod on 2026-07-17
 * (`relay/src/services/positions.ts:836-841`), so the two agree. The stale
 * "whole-cent quantized" claim still carried by `PolyRelayPosition`'s own jsdoc
 * (`src/lib/polymarket/relay.ts`) is wrong and has misled readers into
 * expecting a divergence that does not exist. Deriving from the exact basis is
 * kept because it is the right house rule for COST (avg × count re-multiplied
 * drifts — the I-035 class the Kalshi path already fixed) and because it stays
 * correct if the relay's rounding ever changes again.
 *
 * The one deliberate presentation improvement over Position Tracker is the
 * zero sentinel: Position Tracker can still print the relay's no-basis `0` as
 * "0¢"; this card says "(avg unavailable)" because a zero price is a claim the
 * data does not support. Quantity and non-zero average still come from the same
 * account-store aggregate in both org-key and personal-key modes.
 *
 * ── "No position" is a claim, not a default ─────────────────────────────────
 * An empty row list means nothing until every applicable source has reported.
 * `authoritative` is the gate; a loading, failed, or credential-less source
 * leaves it false so the component says what it does not know instead of
 * asserting the trader is flat.
 */

import type { GameRow } from './types';
import type { UserPositionSlice } from '@/hooks/useUserKalshiPOS';
import type { PolyRelayPosition } from '@/lib/polymarket/relay';
import type { PolyRelayPositionsStatus } from '@/hooks/usePolymarketRelayPositions';
import { sumDecimalText } from '@/lib/polymarketUs/coverage';
import type { UsPositionRow } from '@/lib/polymarketUs/types';

export type PositionVenue = 'kalshi' | 'polymarket' | 'polymarket_us';

/** One held side on one exact market. Never an aggregate. */
export interface GamePositionRow {
  /** `${venue}:${marketId}:${side}` — the dedup identity. */
  key: string;
  venue: PositionVenue;
  /** Kalshi ticker, Polymarket condition id, or Polymarket US market slug. */
  marketId: string;
  /** The market's named (YES) outcome — Kalshi's "(BAL) NO" convention, so a
   *  NO row still names the market's own team. `null` when orientation could
   *  not be resolved; the component says so rather than guessing. */
  outcomeLabel: string | null;
  /** Always the venue-native side. */
  side: 'yes' | 'no';
  /** Fractional on Polymarket. Never rounded here — display quantization is
   *  the component's job. */
  quantity: number;
  /** Canonical before-fees entry in cents at full precision. `null` means
   *  unavailable and is the ONLY way absence is expressed. Always null on
   *  Polymarket US until #1199. */
  averageEntryCents: number | null;
  /** #1233 — Polymarket US only: the exact quantity as decimal text, which is
   *  what the card shows (`quantity` is its number, 0 when unknown). */
  exactQuantity?: {
    /** Null when any account's holding is unknown. */
    text: string | null;
    /** An account's holding is not fully established (the side rail's "*"). */
    partlyKnown: boolean;
    /** Hover: the per-account split (2+ accounts) and why it is partly known. */
    title: string | null;
  };
}

/** #1233 — the US read the snapshot is given: the selected line's exact US
 *  market and the trader's holdings in every own-view account. */
export interface GamePositionUsInput {
  /** The exact US market of the selected line; null while it is not known. */
  marketSlug: string | null;
  /** `absent`: US lists no market on this line, or there is no US for this
   *  login. `error`: the market or a holding could not be read. */
  status: 'absent' | 'loading' | 'ready' | 'error';
  holdings: readonly { accountId: string; positions: readonly UsPositionRow[] }[];
  /** The market's named (YES) outcome, as for Kalshi and Polymarket. */
  outcomeLabel: string | null;
}

/** `absent` = this game lists no market on that venue, so its health is
 *  irrelevant to whether the snapshot is authoritative. */
export type VenueSourceState = 'absent' | 'loading' | 'ready' | 'error' | 'unavailable';

export interface GamePositionProjection {
  rows: GamePositionRow[];
  sources: Record<PositionVenue, VenueSourceState>;
  /** Every applicable source has reported. The ONLY state in which an empty
   *  `rows` may be presented as "no position". */
  authoritative: boolean;
  /** Venues whose rows are on the card while their own read is not `ready`
   *  (failed, or still loading from a previous reading). Those rows are a last
   *  reading, not a current one; another venue's rows say nothing about it
   *  (#1068). */
  lastKnownVenues: PositionVenue[];
}

export interface GamePositionInput {
  /** The panel's EFFECTIVE game — `GameDetailPanel`'s chip-aware local `game`,
   *  never `gameProp`. */
  game: GameRow;
  kalshi: {
    positionByTicker: ReadonlyMap<string, UserPositionSlice>;
    loading: boolean;
    error: string | null;
    /** Increments once per landed snapshot. Read instead of `loading` because
     *  `loading` flips on every 5s poll, which would flicker the copy between
     *  "loading" and "no position" for a trader who simply holds nothing. */
    snapshotSeq: number;
  };
  polymarket: {
    positions: readonly PolyRelayPosition[];
    status: PolyRelayPositionsStatus;
  };
  /** `(conditionId, side)` → outcome label. Injected so this module stays
   *  pure; production passes `polyPositionBetLabel`, which drops the label
   *  rather than guess when book orientation disagrees. */
  polyOutcomeLabel?: (conditionId: string, side: 'yes' | 'no') => string | null;
  /** `(ticker, side)` → the market's named (YES) outcome. Injected for the same
   *  reason: `describeMarket` reads `useMarketStore.getState()`, which would
   *  make this module impure and store-coupled. Production passes a thin
   *  wrapper over `describeMarket(...).betTeam`. Returning `null` degrades to
   *  the game row's slot label, and then to "unavailable" — never to a guess. */
  kalshiOutcomeLabel?: (ticker: string, side: 'yes' | 'no') => string | null;
  /** Absent (or `status: 'absent'`) when the game has no US market. */
  polymarketUs?: GamePositionUsInput;
}

/** Non-positive / non-finite is "no basis", not a price. */
function normalizeAverage(cents: number | null | undefined): number | null {
  return cents != null && Number.isFinite(cents) && cents > 0 ? cents : null;
}

/** Polymarket's canonical average: exact open basis where the relay sent it,
 *  the whole-cent avg only as the stale-payload fallback. */
function polyAverageCents(pos: PolyRelayPosition): number | null {
  const basis = pos.costBasisCents;
  if (basis != null && Number.isFinite(basis) && basis > 0 && pos.netCount > 0) {
    return basis / pos.netCount;
  }
  return normalizeAverage(pos.avgEntryPriceCents);
}

function kalshiState(game: GameRow, k: GamePositionInput['kalshi']): VenueSourceState {
  if (!game.kalshi) return 'absent';
  if (k.error != null) return 'error';
  return k.snapshotSeq > 0 ? 'ready' : 'loading';
}

/**
 * `hasBook` and `conditionIds` answer different questions, and conflating them
 * was a way to hide a real holding: a game that lists a Polymarket book we
 * cannot IDENTIFY (`VenueBook.conditionId` is optional) is not a game with no
 * Polymarket market. Reporting that as `absent` dropped the venue out of the
 * authoritative set, so the card was free to say "No position in this market."
 * over a position it simply could not look up. An unknown is `unavailable`.
 */
function polyState(
  hasBook: boolean,
  conditionIds: Set<string>,
  status: PolyRelayPositionsStatus,
): VenueSourceState {
  if (!hasBook) return 'absent';
  if (conditionIds.size === 0) return 'unavailable';
  switch (status) {
    case 'ready':
      return 'ready';
    case 'error':
      return 'error';
    case 'unavailable':
      return 'unavailable';
    default:
      return 'loading';
  }
}

export function projectGamePosition(input: GamePositionInput): GamePositionProjection {
  const { game, kalshi, polymarket } = input;
  const labelFor = input.polyOutcomeLabel ?? (() => null);
  const kalshiOutcomeLabel = input.kalshiOutcomeLabel ?? (() => null);
  const rows: GamePositionRow[] = [];
  const seen = new Set<string>();

  const push = (row: GamePositionRow) => {
    if (seen.has(row.key)) return;
    seen.add(row.key);
    rows.push(row);
  };

  // ── Kalshi ────────────────────────────────────────────────────────────────
  // A synth spread/total row carries THE SAME ticker in both slots, so the slot
  // a market arrived in says nothing about which outcome its YES names. The
  // ticker is the only orientation authority; the slot label is a fallback, and
  // only for a market that occupies exactly one slot (a moneyline sibling, where
  // the row's own away/home naming is the market's naming).
  const kalshiMarkets: { ticker: string; slotLabel: string }[] = game.kalshi
    ? [
        { ticker: game.kalshi.away.marketTicker, slotLabel: game.awayName || game.awayCode },
        { ticker: game.kalshi.home.marketTicker, slotLabel: game.homeName || game.homeCode },
      ].filter((m) => m.ticker)
    : [];

  const sharedTickers = new Set(
    kalshiMarkets
      .map((m) => m.ticker)
      .filter((t, i, all) => all.indexOf(t) !== i),
  );

  const kalshiLabelFor = (market: { ticker: string; slotLabel: string }, side: 'yes' | 'no') =>
    sharedTickers.has(market.ticker)
      ? // One market filling both slots: the slot it was read out of is not
        // evidence of what its YES names, so the ticker is the only authority.
        kalshiOutcomeLabel(market.ticker, side)
      : // Distinct sibling markets — the away slot holds the away team's own
        // market, so the row's naming IS this market's naming, and it is the
        // richer label ("Kansas City Royals" over the ticker's "KC"). The ticker
        // still backs it up when the row carries no name at all.
        market.slotLabel || kalshiOutcomeLabel(market.ticker, side);

  for (const market of kalshiMarkets) {
    const slice = kalshi.positionByTicker.get(market.ticker);
    if (!slice) continue;
    if (slice.yesCount > 0) {
      push({
        key: `kalshi:${market.ticker}:yes`,
        venue: 'kalshi',
        marketId: market.ticker,
        outcomeLabel: kalshiLabelFor(market, 'yes'),
        side: 'yes',
        quantity: slice.yesCount,
        averageEntryCents: slice.yesAvgCents,
      });
    }
    if (slice.noCount > 0) {
      push({
        key: `kalshi:${market.ticker}:no`,
        venue: 'kalshi',
        marketId: market.ticker,
        outcomeLabel: kalshiLabelFor(market, 'no'),
        side: 'no',
        quantity: slice.noCount,
        averageEntryCents: slice.noAvgCents,
      });
    }
  }

  // ── Polymarket ────────────────────────────────────────────────────────────
  // A moneyline's away and home slots are the SAME market (one condition id,
  // opposite asset ids), so the id set is what identifies the market and the
  // ledger row's own side is what identifies the holding.
  const conditionIds = new Set<string>();
  for (const b of [game.polymarket?.away, game.polymarket?.home]) {
    if (b?.conditionId) conditionIds.add(b.conditionId);
  }

  for (const pos of polymarket.positions) {
    if (!conditionIds.has(pos.ticker)) continue;
    if (!(pos.netCount > 0)) continue;
    push({
      key: `polymarket:${pos.ticker}:${pos.side}`,
      venue: 'polymarket',
      marketId: pos.ticker,
      outcomeLabel: labelFor(pos.ticker, pos.side),
      side: pos.side,
      quantity: pos.netCount,
      averageEntryCents: polyAverageCents(pos),
    });
  }

  // ── Polymarket US ─────────────────────────────────────────────────────────
  // Exact market slug, every outcome held on it (as above), one line per
  // outcome summed across the trader's own-view accounts.
  const us = input.polymarketUs;
  if (us?.marketSlug) {
    for (const row of projectUsHoldings(us.marketSlug, us.holdings, us.outcomeLabel)) push(row);
  }

  const hasPolyBook = !!(game.polymarket?.away || game.polymarket?.home);

  const sources: Record<PositionVenue, VenueSourceState> = {
    kalshi: kalshiState(game, kalshi),
    polymarket: polyState(hasPolyBook, conditionIds, polymarket.status),
    polymarket_us: us?.status ?? 'absent',
  };
  const applicable = Object.values(sources).filter((s) => s !== 'absent');

  return {
    rows,
    sources,
    // `applicable.length > 0` is load-bearing, not defensive: `[].every(...)` is
    // `true`, so a game listing NEITHER venue used to come back authoritative
    // with no rows — i.e. the card asserted "No position in this market." about
    // a game it had no market for at all. Nothing to report is not a report.
    authoritative: applicable.length > 0 && applicable.every((s) => s === 'ready'),
    lastKnownVenues: (Object.keys(sources) as PositionVenue[]).filter(
      (venue) => sources[venue] !== 'ready' && rows.some((row) => row.venue === venue),
    ),
  };
}

/** What a held part is: a positive decimal; unknown (null); or a negative
 *  ledger figure, which means G1's history lacks the buy, so the holding is
 *  unknown, never zero. Zero is no holding (Kalshi and Polymarket show `> 0`). */
const DECIMAL = /^-?\d+(\.\d+)?$/;
const partKind = (quantity: string | null): 'held' | 'unknown' | 'negative' | 'none' => {
  if (quantity === null) return 'unknown';
  if (!DECIMAL.test(quantity) || !/[1-9]/.test(quantity)) return 'none';
  return quantity.startsWith('-') ? 'negative' : 'held';
};
const NEGATIVE_REASON = 'negative ledger figure (G1 history lacks the buy)';

function projectUsHoldings(
  marketSlug: string,
  holdings: GamePositionUsInput['holdings'],
  outcomeLabel: string | null,
): GamePositionRow[] {
  const rows: GamePositionRow[] = [];
  for (const side of ['yes', 'no'] as const) {
    const parts = holdings.flatMap((account) =>
      account.positions
        .filter((p) => p.target.marketSlug === marketSlug && p.target.outcome === side && partKind(p.heldQuantity) !== 'none')
        .map((p) => ({ accountId: account.accountId, position: p })),
    );
    if (parts.length === 0) continue;
    const negative = parts.some((p) => partKind(p.position.heldQuantity) === 'negative');
    // `sumDecimalText` takes non-negative text only, so a negative part is an unknown total.
    const text = negative ? null : sumDecimalText(parts.map((p) => p.position.heldQuantity));
    const reasons = [...new Set([...parts.flatMap((p) => p.position.reasons), ...(negative ? [NEGATIVE_REASON] : [])])];
    const partlyKnown = reasons.length > 0;
    const title = [
      parts.length > 1
        ? `Per account: ${parts.map((p) => `…${p.accountId.slice(-4)} ${p.position.heldQuantity ?? '—'}`).join(', ')}`
        : null,
      partlyKnown ? `Partly known — ${reasons.join(', ')}` : null,
    ].filter(Boolean).join(' · ') || null;
    rows.push({
      key: `polymarket_us:${marketSlug}:${side}`,
      venue: 'polymarket_us',
      marketId: marketSlug,
      outcomeLabel,
      side,
      quantity: text === null ? 0 : Number(text),
      averageEntryCents: null,
      exactQuantity: { text, partlyKnown, title },
    });
  }
  return rows;
}
